import _ from 'lodash';
import moment from 'moment';
import { useEffect, useMemo, useRef } from 'react';
import { convertToMoment } from '@/shared/lib';
import { useMergeState, useUpdateEffect } from '@/shared/lib/customHooks';
import { selectDeviceStripFilter, selectIsLoadingStrip, useReportStore } from '@/entities/report';
import type { SearchAndFilterDeviceEventProps } from '.';

/**
 * Backs the search and filter bar above the device event strip list. Reads the keyed strip filter and loading flag from the report store, debounces the search text into the filter's eventFid after 500 ms, derives the study's default start and stop moments and whether an advanced filter is applied, toggles sort order, and stops keydown propagation from the search input.
 *
 * @param props - Bar props carrying the report store key plus the study start, stop and timezone.
 * @returns An object carrying the strip filter, loading flag, local state, derived date range and sort flags, and the search, sort and filter handlers.
 */
const useSearchAndFilterDeviceEvent = ({
  keyRecoil,
  start,
  stop,
  timezone,
}: Pick<SearchAndFilterDeviceEventProps, 'keyRecoil' | 'start' | 'stop' | 'timezone'>) => {
  const searchWithTimeoutFunc = useRef<any>(null);

  const stripFilter = useReportStore(selectDeviceStripFilter(keyRecoil));
  const setDeviceStripFilterAction = useReportStore((s) => s.actions.setDeviceStripFilter);
  /**
   * Writes the device event strip filter for this tab into the report store. Forwards the value to the store action under the tab's keyRecoil key.
   *
   * @param v - Next filter value or updater accepted by the store action.
   * @returns Nothing.
   */
  const setStripFilter = (v: Parameters<typeof setDeviceStripFilterAction>[1]) =>
    setDeviceStripFilterAction(keyRecoil, v);
  const isLoadingStrip = useReportStore(selectIsLoadingStrip(keyRecoil));

  const [state, setState] = useMergeState<{
    isOpenAdvancedFilterModal: boolean;
    searchValue?: any;
  }>({
    isOpenAdvancedFilterModal: false,
  });

  const isSortDesc = useMemo(() => stripFilter?.order === 'desc', [stripFilter]);

  /**
   * Stores the device event search box text as the user types. Merges the named key and value into local state, from where a debounced effect later pushes it into the strip filter as eventFid.
   *
   * @param name - State key being written, in practice searchValue.
   * @param value - Current text in the search box.
   * @returns Nothing.
   */
  const onChangeSearchValue = (name: any, value: any) => {
    setState({ [name]: value });
  };

  /**
   * Opens the advanced filter modal for device event strips. Sets isOpenAdvancedFilterModal on the local state.
   *
   * @returns Nothing.
   */
  const onClickOpenAdvancedFilter = () => {
    setState({ isOpenAdvancedFilterModal: true });
  };

  /**
   * Flips the device event strip list between ascending and descending order. Merges the inverted order value into the keyed strip filter in the report store.
   *
   * @returns Nothing.
   */
  const onClickSort = () => {
    const order = isSortDesc ? 'asc' : 'desc';
    setStripFilter((prev) => ({ ...prev, order }));
  };

  const defaultStartDate = useMemo(() => convertToMoment(start, timezone), [start, timezone]);

  const defaultEndDate = useMemo(() => convertToMoment(stop, timezone), [stop, timezone]);

  const hasAppliedAdvancedFilter = useMemo(() => {
    const hasChangedStartSearchDate = stripFilter.startSearchDate
      ? moment(stripFilter.startSearchDate).format('MM/DD/YYYY HH:mm') !==
        moment(defaultStartDate).format('MM/DD/YYYY HH:mm')
      : false;
    const hasChangedStopSearchDate = stripFilter.stopSearchDate
      ? moment(stripFilter.stopSearchDate).format('MM/DD/YYYY HH:mm') !==
        moment(defaultEndDate).format('MM/DD/YYYY HH:mm')
      : false;

    const hasAppliedAdvancedFilter =
      hasChangedStartSearchDate ||
      hasChangedStopSearchDate ||
      !_.isNil(stripFilter.eventType) ||
      !_.isNil(stripFilter.isValid);
    return hasAppliedAdvancedFilter;
  }, [stripFilter]);

  /**
   * Translates the advanced filter modal's selection into device strip filter criteria. Rebuilds the start and stop search moments from the picked date and time strings, defaulting to the start and end of day, maps the All type and All validity options to undefined, resets the cursor, closes the modal and merges the result into the keyed strip filter.
   *
   * @param advancedFilterObject - Selection returned by the filter modal with its date, time, event type and validity fields.
   * @returns Nothing.
   */
  const handleClickFilterButton = (advancedFilterObject: any) => {
    const dataFilter: any = { cursor: undefined };

    if (advancedFilterObject.startSearchDate) {
      const startSearchDate = moment(
        `${moment(advancedFilterObject.startSearchDateString, 'MM/DD/YYYY').format('YYYY-MM-DD')}T${
          advancedFilterObject.startSearchTimeString
            ? `${advancedFilterObject.startSearchTimeString}:00`
            : '00:00:00'
        }`,
      );
      _.assign(dataFilter, { startSearchDate });
    }

    if (advancedFilterObject.stopSearchDate) {
      const stopSearchDate = moment(
        `${moment(advancedFilterObject.stopSearchDateString, 'MM/DD/YYYY').format('YYYY-MM-DD')}T${
          advancedFilterObject.stopSearchTimeString
            ? `${advancedFilterObject.stopSearchTimeString}:59`
            : '23:59:59'
        }`,
      );
      _.assign(dataFilter, { stopSearchDate });
    }

    _.assign(dataFilter, {
      eventType:
        advancedFilterObject.eventType === 'All type' ? undefined : advancedFilterObject.eventType,
    });

    _.assign(dataFilter, {
      isValid:
        advancedFilterObject.validity === 'All validity'
          ? undefined
          : advancedFilterObject.validity === 'Valid',
    });

    setState({ isOpenAdvancedFilterModal: false });

    setStripFilter((prev) => ({ ...prev, ...dataFilter }));
  };

  useUpdateEffect(() => {
    if (searchWithTimeoutFunc.current) {
      clearTimeout(searchWithTimeoutFunc.current);
    }
    searchWithTimeoutFunc.current = setTimeout(() => {
      setStripFilter((prev) => ({ ...prev, eventFid: state.searchValue }));
      searchWithTimeoutFunc.current = null;
    }, 500);
  }, [state.searchValue]);

  useEffect(() => {
    const searchInput = document.getElementById('searchValueId');
    if (searchInput) {
      searchInput.addEventListener('keydown', (event) => {
        event.stopPropagation();
      });
    }
  }, []);

  return {
    stripFilter,
    isLoadingStrip,
    state,
    setState,
    isSortDesc,
    defaultStartDate,
    defaultEndDate,
    hasAppliedAdvancedFilter,
    onChangeSearchValue,
    onClickOpenAdvancedFilter,
    onClickSort,
    handleClickFilterButton,
  };
};

export default useSearchAndFilterDeviceEvent;
